import React, { useState } from 'react';

import { Button, Input, Typography } from '@wbk/ui';
import { useTranslation } from 'react-i18next';

import WithGrecaptcha from '@/features/WithGrecaptcha';
import MarqueeSection from './MarqueeSection';

const APP_SCRIPT_URL =
  process.env.VITE_PUBLIC_NEWSLETTER_APP_SCRIPT ||
  'https://script.google.com/macros/s/AKfycbyRAsNCzPv4WhgXeIzkYDEl8UxTKSEo5bDAhCIsA67mZCGb9X8t_A3MNJVy21GnPGTfcw/exec';
const VITE_PUBLIC_CAPTCHA_V3_KEY =
  process.env.VITE_PUBLIC_CAPTCHA_V3_KEY || '6LeelNcrAAAAAMUu7s8zDIuc4dEnXfNaAd2LqyvT';

const Newsletter = () => {
  const { t } = useTranslation(['common']);
  const [email, setEmail] = useState('');
  const [iisSent, setSent] = useState(false);
  const [isSending, setSending] = useState(false);
  const whatsIncluded = [
    {
      label: 'event_alerts',
      icon: () => <img src='/icons/MailNotificationIcon.svg' alt='' />,
    },
    {
      label: 'exclusive_offers',
      icon: () => <img src='/icons/DiscountIcon.svg' alt='' />,
    },
    /*
    {
      label: 'early_access',
      icon: TicketIcon,
    },
   
   */
  ];
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const token = await window.grecaptcha.execute(VITE_PUBLIC_CAPTCHA_V3_KEY, {
        action: 'submit',
      });

      const response = await fetch(APP_SCRIPT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify({
          email,
          token,
          source: 'web',
        }),
      });

      const result = await response.json();

      if (result.ok) {
        setSent(true);
        setEmail('');
      } else {
        console.error('Subscription failed:', result);
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Subscription error:', error, VITE_PUBLIC_CAPTCHA_V3_KEY, APP_SCRIPT_URL);
      alert('Failed to subscribe. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <WithGrecaptcha recaptchaKey={VITE_PUBLIC_CAPTCHA_V3_KEY}>
      <div className='flex flex-col-reverse items-center justify-between gap-8 gap-y-12 md:px-10 lg:flex-row'>
        <div className='flex w-full flex-col space-y-8'>
          {!iisSent && (
            <>
              <div className='space-y-2 px-4 sm:px-0'>
                <Typography
                  as='h4'
                  variant='heading-XL'
                  className='md:text-heading-XXL font-semibold leading-[60%] dark:text-white'
                >
                  {t('common:newsletter.title')}
                </Typography>
                <div className='lg:max-w-sm'>
                  <Typography as='p' variant='heading-M' className='font-semibold text-[#A1A1AA]'>
                    {t('common:newsletter.get_notified')}
                  </Typography>
                </div>
              </div>
              <div className='space-y-6 px-4 sm:px-0'>
                <form
                  className='flex w-full flex-col gap-2 md:max-w-lg md:flex-row md:gap-4'
                  onSubmit={onSubmit}
                >
                  <div className='w-full'>
                    <Input
                      className='w-full rounded border border-white/20 bg-white/20 placeholder:text-[#A1A1AA] hover:bg-white/10'
                      placeholder={t('common:newsletter.email_placeholder')}
                      type='email'
                      name='email'
                      required
                      value={email}
                      onChange={(e) => setEmail(e)}
                    />
                  </div>

                  <Button
                    loading={isSending}
                    type='submit'
                    className='whitespace-nowrap rounded-full !px-10 font-semibold'
                  >
                    {isSending ? t('common:newsletter.sending') : t('common:newsletter.subscribe')}
                  </Button>
                </form>

                <div className='flex flex-col gap-4 text-white'>
                  <Typography as='h6' variant='heading-XS' className='font-semibold uppercase'>
                    {t('common:newsletter.whats_included')}
                  </Typography>
                  <div className='flex flex-wrap gap-2 gap-x-3 sm:items-center sm:gap-4'>
                    {whatsIncluded.map((item) => (
                      <div key={item.label} className='flex items-center gap-3'>
                        <span className='flex size-12 items-center justify-center rounded-full bg-white/10'>
                          <item.icon />
                        </span>
                        <Typography as='p' variant='body-S' className='font-semibold'>
                          {t(`common:newsletter.${item.label}`)}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          {iisSent && (
            <div className='flex items-center justify-center py-20'>
              <Typography
                as='h4'
                variant='heading-XL'
                className='md:text-heading-XXL text-center font-semibold leading-[60%] lg:text-start dark:text-white'
              >
                {t('common:newsletter.thank_you')}
              </Typography>
            </div>
          )}
        </div>

        <MarqueeSection />
      </div>
    </WithGrecaptcha>
  );
};

export default Newsletter;
